import { useEffect, useMemo, useState } from "react";
import DialogAlertAction from "components/DialogAlertAction/DialogAlertAction";
import CustomButton from "components/CustomButton/CustomButton";
import EventFormContext from "views/Eventos/components/FormCreateEvent/ManualLoadingForm/utils/context/EventFormContext";
import Part4Certification from "views/Eventos/components/FormCreateEvent/ManualLoadingForm/components/Steps/Step2/components/Part4Certification/Part4Certification";
import { EVENT_FORM_INITIAL_STATE } from "views/Eventos/components/FormCreateEvent/ManualLoadingForm/reducer/event.reducer";
import {
	requestGetCertificationSignatureTypes,
} from "services/events/certification";
import { requestUpdateEventById } from "services/events/aboutEvents";
import {
	CERTIFICATION_SIGNATURE_SOURCE_STATUS,
	createEmptyAutoCertificate,
	getAutoCertificatePayload,
	getCertificationTeacherReference,
	hasDuplicateSignatureTypes,
	isCertificationSpeakerResolved,
	isInstitutionalCertificationSignature,
	normalizeAutoCertificate,
} from "views/Eventos/utils/certification";
import {
	EditorDialogBody,
	EditorDialogFooterNote,
	EditorDialogMessage,
} from "../styles";

const AUTO_CERTIFICATE_ERROR_TEXT_DEFAULTS = {
	signatures:
		EVENT_FORM_INITIAL_STATE.errorTexts.autoCertificateSignatures,
	teacherSpeaker:
		EVENT_FORM_INITIAL_STATE.errorTexts.autoCertificateTeacherSpeaker,
	validity:
		EVENT_FORM_INITIAL_STATE.errorTexts.autoCertificateValidity,
	signatureCount: "",
	campus: "",
};

const getInitialEditorErrors = () => ({
	...EVENT_FORM_INITIAL_STATE.errors.step2.part4.autoCertificate,
	signatureItems: [],
});

const getInitialErrorTexts = () => ({
	autoCertificateCampus: "",
	autoCertificateSignatureCount:
		AUTO_CERTIFICATE_ERROR_TEXT_DEFAULTS.signatureCount,
	autoCertificateSignatures:
		AUTO_CERTIFICATE_ERROR_TEXT_DEFAULTS.signatures,
	autoCertificateTeacherSpeaker:
		AUTO_CERTIFICATE_ERROR_TEXT_DEFAULTS.teacherSpeaker,
	autoCertificateValidity:
		AUTO_CERTIFICATE_ERROR_TEXT_DEFAULTS.validity,
});

const normalizeStep2FormData = step2Data => {
	if (!step2Data) return null;
	// step2Data ya tiene la estructura por partes (viene del reducer del formulario)
	if (step2Data.part1) {
		return {
			...step2Data,
			part4: step2Data.part4 || {
				autoCertificate:
					step2Data.autoCertificate || createEmptyAutoCertificate(),
			},
		};
	}
	// Estructura plana (compatibilidad con otros orígenes de datos)
	return {
		part1: {
			organizerId: step2Data.organizerId || 0,
			teacher: step2Data.teacher || null,
			payGradeId: step2Data.payGradeId || "",
			placeOrganization: step2Data.placeOrganization || null,
		},
		part2: {
			locationId: step2Data.locationId || 0,
			zoomInscriptionTypeId: step2Data.zoomInscriptionTypeId || 0,
			zoomTypeId: step2Data.zoomTypeId || 0,
			place: step2Data.place || null,
		},
		part3: {
			quantityVacancy: step2Data.quantityVacancy || "",
			publicLoad: step2Data.publicLoad || null,
		},
		part4: {
			autoCertificate:
				step2Data.autoCertificate || createEmptyAutoCertificate(),
		},
	};
};

const buildValidationErrors = ({
	autoCertificate,
	organizerCampusId,
}) => {
	const newErrors = getInitialEditorErrors();

	if (!autoCertificate.enabled) {
		return newErrors;
	}

	const signatureCount = Number(autoCertificate.signatureCount) || 0;
	const selectedSignatures = autoCertificate.signatures.filter(
		({ signatureTypeCode }) => Boolean(signatureTypeCode)
	);

	newErrors.signatureCount = signatureCount < 1 || signatureCount > 3;
	newErrors.signatures = selectedSignatures.length !== signatureCount;
	newErrors.duplicateTypes =
		hasDuplicateSignatureTypes(autoCertificate.signatures);

	if (!organizerCampusId) {
		newErrors.backendCampus = true;
		newErrors.signatures = true;
	}

	newErrors.signatureItems = autoCertificate.signatures.map(signature => ({
		signatureTypeCode: !signature.signatureTypeCode,
		utpCode: false,
		fullName: !isCertificationSpeakerResolved(signature),
	}));

	newErrors.invalidValidity = autoCertificate.signatures.some(
		signature =>
			isInstitutionalCertificationSignature(signature.signatureTypeCode) &&
			signature.sourceStatus ===
				CERTIFICATION_SIGNATURE_SOURCE_STATUS.INVALID_CAMPUS_OR_EVENT_END_DATE
	);

	return newErrors;
};

const hasEditorErrors = errors =>
	Boolean(
		errors.signatureCount ||
			errors.signatures ||
			errors.duplicateTypes ||
			errors.invalidValidity ||
			errors.backendCampus ||
			errors.signatureItems.some(
				item => item.signatureTypeCode || item.fullName || item.utpCode
			)
	);

const applyCertificationBackendError = (certificationError, setErrors, setErrorTexts) => {
	if (!certificationError) return false;

	setErrors(prev => {
		const next = {
			...prev,
			signatureItems: [...prev.signatureItems],
		};
		switch (certificationError.scope) {
			case "campus":
				next.backendCampus = true;
				break;
			case "signatureCount":
				next.signatureCount = true;
				break;
			case "duplicateTypes":
				next.duplicateTypes = true;
				break;
			case "teacherSpeaker":
				next.backendTeacherSpeaker = true;
				break;
			case "institutionalValidity":
				next.invalidValidity = true;
				break;
			case "signatures":
			default:
				next.signatures = true;
				break;
		}
		return next;
	});

	setErrorTexts(prev => {
		const next = { ...prev };
		switch (certificationError.scope) {
			case "campus":
				next.autoCertificateCampus = certificationError.text;
				break;
			case "signatureCount":
				next.autoCertificateSignatureCount = certificationError.text;
				break;
			case "teacherSpeaker":
				next.autoCertificateTeacherSpeaker = certificationError.text;
				break;
			case "institutionalValidity":
				next.autoCertificateValidity = certificationError.text;
				break;
			case "signatures":
			default:
				next.autoCertificateSignatures = certificationError.text;
				break;
		}
		return next;
	});

	return true;
};

const PastEventCertificationEditorDialog = ({
	show,
	onClose,
	eventId,
	step2Data,
	eventEndDate,
	currentAutoCertificate,
	organizerCampusName = "",
	certificationSignatureTypes: initialCertificationSignatureTypes = [],
	loadingRequest,
	onSaved,
}) => {
	const [certificationSignatureTypes, setCertificationSignatureTypes] = useState(
		initialCertificationSignatureTypes
	);
	const [editorAutoCertificate, setEditorAutoCertificate] = useState(
		createEmptyAutoCertificate()
	);
	const [errors, setErrors] = useState(getInitialEditorErrors());
	const [errorTexts, setErrorTexts] = useState(getInitialErrorTexts());
	const [submitMessage, setSubmitMessage] = useState("");
	const [isSaving, setIsSaving] = useState(false);
	const normalizedStep2Data = useMemo(
		() => normalizeStep2FormData(step2Data),
		[step2Data]
	);

	const step2OrganizerCampus =
		normalizedStep2Data?.part1?.placeOrganization?.campus || null;
	const organizerCampus = {
		id:
			step2OrganizerCampus?.id ||
			currentAutoCertificate?.campusId ||
			editorAutoCertificate?.campusId ||
			0,
		name:
			step2OrganizerCampus?.name ||
			organizerCampusName ||
			"",
	};
	const teacher = normalizedStep2Data?.part1?.teacher || null;
	const teacherReference = useMemo(
		() => getCertificationTeacherReference(teacher),
		[teacher]
	);

	useEffect(() => {
		if (!show) return;
		setEditorAutoCertificate(
			normalizeAutoCertificate(currentAutoCertificate || createEmptyAutoCertificate())
		);
		setErrors(getInitialEditorErrors());
		setErrorTexts(getInitialErrorTexts());
		setSubmitMessage("");
	}, [show, currentAutoCertificate]);

	useEffect(() => {
		if (!show || certificationSignatureTypes.length > 0) return;
		let isMounted = true;
		const fillSignatureTypes = async () => {
			const data = await requestGetCertificationSignatureTypes();
			if (!isMounted) return;
			setCertificationSignatureTypes(data || []);
		};
		fillSignatureTypes();
		return () => {
			isMounted = false;
		};
	}, [show, certificationSignatureTypes.length, initialCertificationSignatureTypes]);

	const certificationActions = useMemo(
		() => ({
			setEnabled: enabled => {
				setEditorAutoCertificate(prev => ({
					...prev,
					enabled,
				}));
			},
			setField: (field, value) => {
				setEditorAutoCertificate(prev => ({
					...prev,
					[field]: value,
				}));
			},
			setSignatureCount: value => {
				setEditorAutoCertificate(prev =>
					normalizeAutoCertificate({
						...prev,
						signatureCount: value,
						enabled: true,
					})
				);
			},
			setSignature: (index, value) => {
				setEditorAutoCertificate(prev =>
					normalizeAutoCertificate({
						...prev,
						signatures: prev.signatures.map((signature, currentIndex) =>
							currentIndex === index
								? {
										...signature,
										...value,
								  }
								: signature
						),
					})
				);
			},
			replace: value => {
				setEditorAutoCertificate(normalizeAutoCertificate(value));
			},
		}),
		[]
	);

	const initialAutoCertificatePayload = useMemo(
		() =>
			JSON.stringify(
				getAutoCertificatePayload(
					normalizeAutoCertificate(
						currentAutoCertificate || createEmptyAutoCertificate()
					),
					teacherReference
				)
			),
		[currentAutoCertificate, teacherReference]
	);

	const currentAutoCertificatePayload = useMemo(
		() =>
			JSON.stringify(
				getAutoCertificatePayload(editorAutoCertificate, teacherReference)
			),
		[editorAutoCertificate, teacherReference]
	);

	const hasConfigurationChanges =
		initialAutoCertificatePayload !== currentAutoCertificatePayload;

	const handleDialogClose = (_, reason) => {
		if (isSaving) return;
		if (reason === "backdropClick" || reason === "escapeKeyDown") return;
		onClose?.();
	};

	const handleSave = async () => {
		if (!normalizedStep2Data) return;
		if (!hasConfigurationChanges) return;

		const nextErrors = buildValidationErrors({
			autoCertificate: editorAutoCertificate,
			organizerCampusId: organizerCampus.id,
		});
		setErrors(nextErrors);
		setErrorTexts(getInitialErrorTexts());
		setSubmitMessage("");

		if (hasEditorErrors(nextErrors)) return;

		setIsSaving(true);
		loadingRequest.show("Guardando configuración de constancias...");
		try {
			const nextStep2Data = {
				...normalizedStep2Data,
				part1: {
					...normalizedStep2Data.part1,
					placeOrganization: {
						...(normalizedStep2Data.part1?.placeOrganization || {}),
						campus: {
							id: organizerCampus.id,
							name: organizerCampus.name || "",
						},
					},
				},
			};
			const payload = {
				...nextStep2Data,
				part4: {
					autoCertificate: editorAutoCertificate,
				},
			};
			const result = await requestUpdateEventById(eventId, payload, "step2");
			if (result?.certificationError) {
				applyCertificationBackendError(
					result.certificationError,
					setErrors,
					setErrorTexts
				);
				return;
			}
			if (!result?.success) {
				setSubmitMessage(
					"Hubo un problema al guardar la configuración. Inténtalo nuevamente."
				);
				return;
			}
			onSaved?.();
			onClose?.();
		} finally {
			loadingRequest.hide();
			setIsSaving(false);
		}
	};

	return (
		<DialogAlertAction
			show={show}
			toggleShow={() => {}}
			close={handleDialogClose}
			noImage
			title=""
			width="980px"
			widthContent="100%"
			contentComponent={
				<EditorDialogBody>
					<EventFormContext.Provider
						value={{
							data: {
								certificationSignatureTypes,
							},
							isCreate: false,
							loadingRequest,
							isWorkshopEventType: false,
						}}
					>
						<Part4Certification
							form={{ autoCertificate: editorAutoCertificate }}
							errors={errors}
							errorTexts={errorTexts}
							organizerCampus={organizerCampus}
							eventEndDate={eventEndDate}
							teacher={teacher}
							certificationActions={certificationActions}
						/>
					</EventFormContext.Provider>
					<EditorDialogFooterNote>
						Este ajuste solo modifica la configuración de constancias para futuros reprocesos.
					</EditorDialogFooterNote>
					{submitMessage ? (
						<EditorDialogMessage>{submitMessage}</EditorDialogMessage>
					) : null}
				</EditorDialogBody>
			}
		>
			<CustomButton
				variant="quaternary"
				text="Cancelar"
				disabled={isSaving}
				onClick={handleDialogClose}
			/>
			<CustomButton
				text="Guardar"
				disabled={isSaving || !hasConfigurationChanges}
				onClick={handleSave}
			/>
		</DialogAlertAction>
	);
};

export default PastEventCertificationEditorDialog;
